const schemaGraphElementId = "schema-org-graph";

function normalizeProductUrl(url) {
  if (!url) {
    return null;
  }

  try {
    const normalizedUrl = new URL(url, document.baseURI);
    normalizedUrl.search = "";
    normalizedUrl.hash = "";
    return normalizedUrl.href;
  } catch {
    return null;
  }
}

function findProductGroup(graph) {
  const webPage = graph.find((node) => node?.["@type"] === "WebPage");
  const productGroupId = webPage?.mainEntity?.["@id"];

  if (!productGroupId) {
    return null;
  }

  return graph.find(
    (node) => node?.["@type"] === "ProductGroup" && node?.["@id"] === productGroupId
  );
}

function getArticleTitle(article) {
  return article?.dataCollection?.find(
    (item) => item?.fieldName === "ItemCategoryTitle"
  )?.fieldValue;
}

function createVariantUrl(productUrl, productCode, articleCode) {
  try {
    const variantUrl = new URL(productUrl, document.baseURI);
    variantUrl.search = "";
    variantUrl.searchParams.set("code", productCode);
    variantUrl.searchParams.set("am", articleCode);
    variantUrl.hash = "parts";
    return variantUrl.href;
  } catch {
    return null;
  }
}

function createProductVariant(article, productGroup, productCode, series, manufacturerId) {
  const articleCode = article?.code;
  const productUrl = normalizeProductUrl(productGroup?.url);
  const resolvedProductCode = productCode || productGroup?.productGroupID;

  if (!articleCode || !productUrl || !resolvedProductCode) {
    return null;
  }

  const articleTitle = getArticleTitle(article);
  const name = articleTitle
    ? `${articleTitle} - ${articleCode}`
    : article?.name || articleCode;
  const variant = {
    "@type": "Product",
    "@id": `${productUrl}#variant-${encodeURIComponent(articleCode)}`,
    sku: articleCode,
    name,
    url: createVariantUrl(productUrl, resolvedProductCode, articleCode),
    isVariantOf: { "@id": productGroup["@id"] },
  };

  // Series content links use the same serialized ID format as SeriesListing.
  const modelNames = [...new Set(
    series
      .filter((item) => article.ancestorNodes?.includes(item?.contentLink?.split("__")[0]))
      .map((item) => item.name?.trim())
      .filter(Boolean)
  )];
  if (modelNames.length > 0) {
    variant.model = modelNames.length === 1 ? modelNames[0] : modelNames;
  }

  if (manufacturerId) {
    variant.manufacturer = { "@id": manufacturerId };
  }

  const imageUrl = resolveImageUrl(article.imageUrl, productUrl);
  if (imageUrl) {
    variant.image = imageUrl;
  }

  if (!variant.url) {
    delete variant.url;
  }

  return variant;
}

function createAdditionalProperties(properties) {
  if (!Array.isArray(properties)) {
    return [];
  }

  const propertyKeys = new Set();
  const additionalProperties = [];

  properties.forEach((property) => {
    const name = property?.fieldHeader?.trim();
    const value = property?.fieldValue?.replaceAll("|", ", ")?.trim();
    const propertyKey = `${name}\u0000${value}`;

    if (!name || !value || propertyKeys.has(propertyKey)) {
      return;
    }

    propertyKeys.add(propertyKey);
    additionalProperties.push({
      "@type": "PropertyValue",
      name,
      value,
    });
  });

  return additionalProperties;
}

export function updateProductGroupVariants(articles, productCode, series = []) {
  const schemaElement = document.getElementById(schemaGraphElementId);
  if (!schemaElement || !Array.isArray(articles)) {
    return false;
  }

  try {
    const schemaDocument = JSON.parse(schemaElement.textContent);
    const graph = schemaDocument?.["@graph"];
    if (!Array.isArray(graph)) {
      return false;
    }

    const productGroup = findProductGroup(graph);
    if (!productGroup) {
      return false;
    }

    const manufacturerId = graph.find(
      (node) => node?.["@type"] === "WebPage" && node?.mainEntity?.["@id"] === productGroup["@id"]
    )?.publisher?.["@id"];

    const variantIds = new Set(
      Array.isArray(productGroup.hasVariant)
        ? productGroup.hasVariant.map((variant) => variant?.["@id"]).filter(Boolean)
        : []
    );
    const variantNodes = new Map(
      graph
        .filter((node) => variantIds.has(node?.["@id"]) && node?.["@type"] === "Product")
        .map((node) => [node["@id"], node])
    );

    articles.forEach((article) => {
      const variant = createProductVariant(article, productGroup, productCode, series, manufacturerId);
      if (!variant) {
        return;
      }

      variantIds.add(variant["@id"]);
      variantNodes.set(variant["@id"], variant);
    });

    if (variantIds.size > 0) {
      productGroup.hasVariant = Array.from(variantIds, (variantId) => ({ "@id": variantId }));
    } else {
      delete productGroup.hasVariant;
    }

    schemaDocument["@graph"] = graph
      .filter((node) => !variantIds.has(node?.["@id"]) || node?.["@type"] !== "Product")
      .concat(Array.from(variantNodes.values()));
    schemaElement.textContent = JSON.stringify(schemaDocument);
    return true;
  } catch {
    return false;
  }
}

export function enrichProductVariantProperties(article) {
  const schemaElement = document.getElementById(schemaGraphElementId);
  if (!schemaElement || !article?.code) {
    return false;
  }

  try {
    const schemaDocument = JSON.parse(schemaElement.textContent);
    const graph = schemaDocument?.["@graph"];
    if (!Array.isArray(graph)) {
      return false;
    }

    const productGroup = findProductGroup(graph);
    const productUrl = normalizeProductUrl(productGroup?.url);
    if (!productGroup || !productUrl) {
      return false;
    }

    const variantId = `${productUrl}#variant-${encodeURIComponent(article.code)}`;
    const variant = graph.find(
      (node) => node?.["@type"] === "Product" && node?.["@id"] === variantId
    );
    if (!variant) {
      return false;
    }

    const additionalProperties = createAdditionalProperties(article.formattedDataCollection);
    if (additionalProperties.length > 0) {
      variant.additionalProperty = additionalProperties;
    } else {
      delete variant.additionalProperty;
    }

    schemaElement.textContent = JSON.stringify(schemaDocument);
    return true;
  } catch {
    return false;
  }
}

function resolveImageUrl(imageUrl, productUrl) {
  if (!imageUrl) {
    return null;
  }

  const cdnHost = typeof i18n !== "undefined" ? i18n.CdnHostName : null;

  try {
    return new URL(imageUrl, cdnHost || productUrl || document.baseURI).href;
  } catch {
    return null;
  }
}

function createProductGroupListItem(product, position) {
  const productUrl = normalizeProductUrl(product?.url);
  const name = product?.name || product?.heading || product?.code;

  if (!productUrl || !name) {
    return null;
  }

  const productGroup = {
    "@type": "ProductGroup",
    "@id": `${productUrl}#productgroup`,
    url: productUrl,
    name,
  };

  if (product.code) {
    productGroup.productGroupID = product.code;
  }

  if (product.excerpt) {
    productGroup.description = product.excerpt;
  }

  const imageUrl = resolveImageUrl(product.imageUrl, productUrl);
  if (imageUrl) {
    productGroup.image = imageUrl;
  }

  return {
    "@type": "ListItem",
    position,
    item: productGroup,
  };
}

function findItemList(graph) {
  const collectionPage = graph.find((node) => node?.["@type"] === "CollectionPage");
  const itemListId = collectionPage?.mainEntity?.["@id"];

  if (itemListId) {
    return graph.find(
      (node) => node?.["@type"] === "ItemList" && node?.["@id"] === itemListId
    );
  }

  return null;
}

export function updateProductGroupItemList(products) {
  const schemaElement = document.getElementById(schemaGraphElementId);
  if (!schemaElement || !Array.isArray(products)) {
    return false;
  }

  try {
    const schemaDocument = JSON.parse(schemaElement.textContent);
    const graph = schemaDocument?.["@graph"];
    if (!Array.isArray(graph)) {
      return false;
    }

    const itemList = findItemList(graph);
    if (!itemList) {
      return false;
    }

    const productGroupIds = new Set();
    const itemListElements = [];

    products.forEach((product) => {
      const listItem = createProductGroupListItem(product, itemListElements.length + 1);
      const productGroupId = listItem?.item?.["@id"];

      if (!listItem || productGroupIds.has(productGroupId)) {
        return;
      }

      productGroupIds.add(productGroupId);
      itemListElements.push(listItem);
    });

    itemList.numberOfItems = itemListElements.length;
    itemList.itemListElement = itemListElements;
    schemaElement.textContent = JSON.stringify(schemaDocument);
    return true;
  } catch {
    return false;
  }
}
